import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotification } from "./notification.hook";
import TechSkillsService from "../services/api/techSkills/TechSkills.service";
import {
  RefreshReactQuery,
  TECH_SKILLS_PAGE_SIZE,
} from "../constants/AppConstants";
import { useEffect } from "react";
import {
  DATA_DELETED_SUCCESS_LABEL,
  DATA_UPDATED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../constants/Dictionary";
import {
  AddTechSkillRequest,
  UpdateTechSkillRequest,
} from "../types/api/techSkills.type";

const TECH_SKILLS_QUERY_KEY = "get-tech-skills";

interface useTechSkillsProps {
  paginationTechSkills: number;
  debounceSearchTechSkill: string;
}

export const useTechSkills = ({
  paginationTechSkills,
  debounceSearchTechSkill,
}: useTechSkillsProps) => {
  const { notification } = useNotification();

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      TECH_SKILLS_QUERY_KEY,
      paginationTechSkills,
      debounceSearchTechSkill,
    ],
    queryFn: () =>
      TechSkillsService.list(TECH_SKILLS_PAGE_SIZE, paginationTechSkills, [
        ...(debounceSearchTechSkill
          ? [{ key: "search", value: [debounceSearchTechSkill] }]
          : []),
      ]),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useAddTechSkill = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: addTechSkill, isPending: isAdding } = useMutation({
    mutationKey: ["add-tech-skill"],
    mutationFn: (body: AddTechSkillRequest) => TechSkillsService.add(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TECH_SKILLS_QUERY_KEY] });
    },
  });

  return { addTechSkill, isAdding };
};

export const useUpdateTechSkill = () => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync: updateTechSkill, isPending: isUpdating } = useMutation({
    mutationKey: ["update-tech-skill"],
    mutationFn: ({ id, body }: { id: number; body: UpdateTechSkillRequest }) =>
      TechSkillsService.update(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TECH_SKILLS_QUERY_KEY] });
      notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
    },
  });

  return { updateTechSkill, isUpdating };
};

export const useDeleteTechSkill = () => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteTechSkill, isPending: isDeleting } = useMutation({
    mutationKey: ["delete-tech-skill"],
    mutationFn: (skillId: number) => TechSkillsService.delete(skillId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TECH_SKILLS_QUERY_KEY] });
      notification.success(DATA_DELETED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error deleting technical skills: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { deleteTechSkill, isDeleting };
};
